"use client";

import { usePathname, useSearchParams } from "next/navigation";
import {
  ANALYTICS_CLIENT_EVENT,
  type ClientAnalyticsEventDetail,
} from "./trackAnalyticsEvent";

import { type ReactNode, useCallback, useEffect, useMemo, useRef } from "react";

const ANALYTICS_ENDPOINT = "/api/analytics/events/";
const ANALYTICS_BATCH_DELAY_MS = 700;
const ANALYTICS_BATCH_MAX_SIZE = 10;
const SESSION_STORAGE_KEY = "mdsafar.analytics.session-id";
const TRAFFIC_ATTRIBUTION_STORAGE_KEY = "mdsafar.analytics.traffic-attribution";
const TRAFFIC_ATTRIBUTION_COOKIE_NAME = "mdsafar_traffic_attribution";
const TRAFFIC_ATTRIBUTION_MAX_AGE_SECONDS = 60 * 60 * 24 * 30;
const VISITOR_STORAGE_KEY = "mdsafar.analytics.visitor-id";
const VISITOR_COOKIE_NAME = "mdsafar_visitor_id";
const FIRST_SEEN_COOKIE_NAME = "mdsafar_first_seen_at";
const VISITOR_COOKIE_MAX_AGE_SECONDS = 60 * 60 * 24 * 365;
const SCROLL_THRESHOLDS = [25, 50, 75, 90] as const;

type AnalyticsEventName =
  | "page_view"
  | "page_leave"
  | "scroll_depth"
  | "cta_click"
  | "banner_click"
  | "search_submitted"
  | "filter_changed"
  | "sort_changed"
  | "hotel_result_clicked"
  | "hotel_detail_viewed"
  | "hotel_room_selected"
  | "passengers_step_viewed"
  | "confirm_step_viewed"
  | "payment_started"
  | "purchase_completed"
  | "purchase_failed"
  | string;

type AnalyticsEventPayload = {
  eventId: string;
  visitorId: string;
  sessionId: string;
  eventName: AnalyticsEventName;
  occurredAt: string;
  path: string;
  url: string;
  pageTitle: string;
  referrer: string;
  device: string;
  utm: {
    source: string;
    medium: string;
    campaign: string;
    content: string;
    term: string;
    id: string;
  };
  element?: {
    id?: string;
    name?: string;
    type?: string;
  };
  funnel?: string;
  step?: string;
  properties?: Record<string, unknown>;
};

type PageState = {
  path: string;
  url: string;
  title: string;
  referrer: string;
  startedAt: number;
  startedAtIso: string;
};

let analyticsQueue: AnalyticsEventPayload[] = [];
let analyticsFlushTimer: number | null = null;

export default function AnalyticsProvider({
  children,
}: {
  children: ReactNode;
}) {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const pathWithSearch = useMemo(() => {
    const query = searchParams.toString();
    return query ? `${pathname}?${query}` : pathname;
  }, [pathname, searchParams]);
  const identityRef = useRef<{
    visitorId: string;
    sessionId: string;
  } | null>(null);
  const pageRef = useRef<PageState | null>(null);
  const scrollThresholdsRef = useRef<Set<number>>(new Set());

  const sendEvent = useCallback(
    (
      eventName: AnalyticsEventName,
      options: {
        page?: PageState;
        element?: AnalyticsEventPayload["element"];
        funnel?: string;
        step?: string;
        properties?: Record<string, unknown>;
      } = {},
    ) => {
      const identity = identityRef.current ?? getAnalyticsIdentity();
      identityRef.current = identity;

      if (!identity.visitorId || !identity.sessionId) return;

      const page = options.page ?? getCurrentPageState(pathWithSearch);
      const payload: AnalyticsEventPayload = {
        eventId: createEventId(),
        visitorId: identity.visitorId,
        sessionId: identity.sessionId,
        eventName,
        occurredAt: new Date().toISOString(),
        path: page.path,
        url: page.url,
        pageTitle: page.title,
        referrer: page.referrer,
        device: getCookieValue("device"),
        utm: getTrafficAttribution(page),
        element: options.element,
        funnel: options.funnel,
        step: options.step,
        properties: options.properties,
      };

      postAnalyticsEvent(payload);
    },
    [pathWithSearch],
  );

  useEffect(() => {
    identityRef.current = getAnalyticsIdentity();
  }, []);

  useEffect(() => {
    if (!identityRef.current) identityRef.current = getAnalyticsIdentity();

    const previousPage = pageRef.current;
    if (previousPage) {
      sendEvent("page_leave", {
        page: previousPage,
        properties: {
          durationMs: Math.max(
            0,
            Math.round(performance.now() - previousPage.startedAt),
          ),
          nextPath: pathWithSearch,
        },
      });
    }

    const currentPage = getCurrentPageState(pathWithSearch);
    pageRef.current = currentPage;
    scrollThresholdsRef.current = new Set();
    sendEvent("page_view", {
      page: currentPage,
      properties: {
        startedAt: currentPage.startedAtIso,
      },
    });
  }, [pathWithSearch, sendEvent]);

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState !== "hidden" || !pageRef.current) return;

      sendEvent("page_leave", {
        page: pageRef.current,
        properties: {
          durationMs: Math.max(
            0,
            Math.round(performance.now() - pageRef.current.startedAt),
          ),
          reason: "visibility_hidden",
        },
      });
      flushAnalyticsEvents(true);
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [sendEvent]);

  useEffect(() => {
    let ticking = false;

    const handleScroll = () => {
      if (ticking) return;

      ticking = true;
      window.requestAnimationFrame(() => {
        ticking = false;
        const maxScroll =
          document.documentElement.scrollHeight - window.innerHeight;

        if (maxScroll <= 0) return;

        const scrollPercent = Math.min(
          100,
          Math.round((window.scrollY / maxScroll) * 100),
        );

        for (const threshold of SCROLL_THRESHOLDS) {
          if (
            scrollPercent >= threshold &&
            !scrollThresholdsRef.current.has(threshold)
          ) {
            scrollThresholdsRef.current.add(threshold);
            sendEvent("scroll_depth", {
              properties: {
                threshold,
                scrollPercent,
              },
            });
          }
        }
      });
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    handleScroll();

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, [sendEvent]);

  useEffect(() => {
    const handleAnalyticsEvent = (event: Event) => {
      const detail = (event as CustomEvent<ClientAnalyticsEventDetail>).detail;
      if (!detail?.eventName) return;

      sendEvent(detail.eventName, {
        element: detail.element,
        funnel: detail.funnel,
        step: detail.step,
        properties: detail.properties,
      });
    };

    window.addEventListener(ANALYTICS_CLIENT_EVENT, handleAnalyticsEvent);

    return () => {
      window.removeEventListener(ANALYTICS_CLIENT_EVENT, handleAnalyticsEvent);
    };
  }, [sendEvent]);

  useEffect(() => {
    const handleClick = (event: MouseEvent) => {
      const target = event.target;
      if (!(target instanceof Element)) return;

      const element = target.closest<HTMLElement>("[data-analytics-event]");
      if (!element) return;

      const eventName = element.dataset.analyticsEvent;
      if (!eventName) return;

      sendEvent(eventName, {
        element: {
          id: element.dataset.analyticsId ?? element.id,
          name:
            element.dataset.analyticsName ??
            element.getAttribute("aria-label") ??
            element.textContent?.trim().slice(0, 120),
          type: element.dataset.analyticsType ?? element.tagName.toLowerCase(),
        },
        funnel: element.dataset.analyticsFunnel,
        step: element.dataset.analyticsStep,
        properties: readAnalyticsProperties(element),
      });
    };

    document.addEventListener("click", handleClick, true);

    return () => {
      document.removeEventListener("click", handleClick, true);
    };
  }, [sendEvent]);

  return children;
}

function getAnalyticsIdentity() {
  const visitorId =
    getCookieValue(VISITOR_COOKIE_NAME) ||
    getCookieValue("user-id") ||
    getLocalVisitorId();

  ensureAnalyticsCookies(visitorId);

  return {
    visitorId,
    sessionId: getSessionId(),
  };
}

function getSessionId() {
  const current = window.sessionStorage.getItem(SESSION_STORAGE_KEY);
  if (current) return current;

  const next = `sess_${crypto.randomUUID().replace(/-/g, "")}`;
  window.sessionStorage.setItem(SESSION_STORAGE_KEY, next);
  return next;
}

function getLocalVisitorId() {
  const current = window.localStorage.getItem(VISITOR_STORAGE_KEY);
  if (current) return current;

  const next = `local_${crypto.randomUUID().replace(/-/g, "")}`;
  window.localStorage.setItem(VISITOR_STORAGE_KEY, next);
  return next;
}

function ensureAnalyticsCookies(visitorId: string) {
  if (!visitorId) return;

  if (!getCookieValue(VISITOR_COOKIE_NAME)) {
    setCookie(VISITOR_COOKIE_NAME, visitorId, VISITOR_COOKIE_MAX_AGE_SECONDS);
  }

  if (!getCookieValue(FIRST_SEEN_COOKIE_NAME)) {
    setCookie(
      FIRST_SEEN_COOKIE_NAME,
      new Date().toISOString(),
      VISITOR_COOKIE_MAX_AGE_SECONDS,
    );
  }
}

function getCurrentPageState(path: string): PageState {
  return {
    path,
    url: window.location.href,
    title: document.title,
    referrer: document.referrer,
    startedAt: performance.now(),
    startedAtIso: new Date().toISOString(),
  };
}

function createEventId() {
  return `evt_${crypto.randomUUID().replace(/-/g, "")}`;
}

function getCookieValue(name: string) {
  const cookie = document.cookie
    .split("; ")
    .find((item) => item.startsWith(`${name}=`));

  return cookie ? decodeURIComponent(cookie.split("=").slice(1).join("=")) : "";
}

function setCookie(name: string, value: string, maxAgeSeconds: number) {
  document.cookie = `${name}=${encodeURIComponent(
    value,
  )}; Max-Age=${maxAgeSeconds}; Path=/; SameSite=Lax`;
}

function getTrafficAttribution(page: PageState) {
  const storedAttribution = getStoredTrafficAttribution();
  const urlUtms = getUtmsFromUrl(page.url);
  const referrerAttribution = getReferrerAttribution(page.referrer);
  const hasUrlUtm = Boolean(urlUtms.source);
  const hasExternalReferrer = referrerAttribution.source !== "direct";
  const isPaymentReturn = isPaymentReferrer(page.referrer);

  if (isPaymentReturn && storedAttribution) {
    return storedAttribution;
  }

  if (!hasUrlUtm && (!hasExternalReferrer || isPaymentReturn) && storedAttribution) {
    return storedAttribution;
  }

  const attribution = hasUrlUtm
    ? {
        source: urlUtms.source,
        medium: urlUtms.medium || "campaign",
        campaign: urlUtms.campaign,
        content: urlUtms.content,
        term: urlUtms.term,
        id:
          urlUtms.id ||
          getCookieValue("utm_id") ||
          getCookieValue("safarmarketId"),
      }
    : isPaymentReturn
      ? {
          source: "direct",
          medium: "none",
          campaign: "",
          content: "",
          term: "",
          id: "",
        }
      : {
          source: referrerAttribution.source,
          medium: referrerAttribution.medium,
          campaign: referrerAttribution.campaign,
          content: "",
          term: "",
          id: "",
        };

  storeTrafficAttribution(attribution);
  return attribution;
}

function getStoredTrafficAttribution() {
  const raw =
    readStorageItem(window.sessionStorage, TRAFFIC_ATTRIBUTION_STORAGE_KEY) ||
    readStorageItem(window.localStorage, TRAFFIC_ATTRIBUTION_STORAGE_KEY) ||
    getCookieValue(TRAFFIC_ATTRIBUTION_COOKIE_NAME);

  if (!raw) return null;

  try {
    const value = JSON.parse(raw) as ReturnType<typeof getEmptyUtms>;

    return {
      ...getEmptyUtms(),
      ...value,
    };
  } catch {
    return null;
  }
}

function storeTrafficAttribution(attribution: ReturnType<typeof getEmptyUtms>) {
  const value = JSON.stringify(attribution);

  try {
    window.sessionStorage.setItem(TRAFFIC_ATTRIBUTION_STORAGE_KEY, value);
  } catch {
    // Analytics should never block page interaction.
  }

  try {
    window.localStorage.setItem(TRAFFIC_ATTRIBUTION_STORAGE_KEY, value);
  } catch {
    // Analytics should never block page interaction.
  }

  setCookie(
    TRAFFIC_ATTRIBUTION_COOKIE_NAME,
    value,
    TRAFFIC_ATTRIBUTION_MAX_AGE_SECONDS,
  );
}

function readStorageItem(storage: Storage, key: string) {
  try {
    return storage.getItem(key);
  } catch {
    return null;
  }
}

function getEmptyUtms() {
  return {
    source: "",
    medium: "",
    campaign: "",
    content: "",
    term: "",
    id: "",
  };
}

function getUtmsFromUrl(url: string) {
  try {
    const params = new URL(url).searchParams;

    return {
      source: params.get("utm_source") ?? "",
      medium: params.get("utm_medium") ?? "",
      campaign: params.get("utm_campaign") ?? "",
      content: params.get("utm_content") ?? "",
      term: params.get("utm_term") ?? "",
      id: params.get("utm_id") ?? "",
    };
  } catch {
    return getEmptyUtms();
  }
}

function getReferrerAttribution(referrer: string) {
  const referrerHost = getExternalReferrerHost(referrer);

  if (!referrerHost || isPaymentHost(referrerHost)) {
    return {
      source: "direct",
      medium: "none",
      campaign: "",
    };
  }

  const searchSource = getSearchEngineSource(referrerHost);
  if (searchSource) {
    return {
      source: searchSource,
      medium: "organic",
      campaign: "",
    };
  }

  const socialSource = getSocialSource(referrerHost);
  if (socialSource) {
    return {
      source: socialSource,
      medium: "social",
      campaign: "",
    };
  }

  return {
    source: referrerHost,
    medium: "referral",
    campaign: "",
  };
}

function isPaymentReferrer(referrer: string) {
  const host = getExternalReferrerHost(referrer);
  return Boolean(host && isPaymentHost(host));
}

function isPaymentHost(host: string) {
  return PAYMENT_REFERRER_HOST_KEYWORDS.some((keyword) =>
    host.includes(keyword),
  );
}

const PAYMENT_REFERRER_HOST_KEYWORDS = [
  "shaparak",
  "sadad",
  "behpardakht",
  "bpm.",
  "pec.",
  "pep.",
  "sep.",
  "asanpardakht",
  "samanepay",
  "sepehr",
  "ikc.",
  "zarinpal",
  "idpay",
  "nextpay",
  "pay.ir",
] as const;

function getExternalReferrerHost(referrer: string) {
  if (!referrer) return "";

  try {
    const referrerUrl = new URL(referrer);
    if (referrerUrl.hostname === window.location.hostname) return "";

    return referrerUrl.hostname.replace(/^www\./, "").toLowerCase();
  } catch {
    return "";
  }
}

function getSearchEngineSource(host: string) {
  if (host.includes("google.")) return "google";
  if (host.includes("bing.")) return "bing";
  if (host.includes("yahoo.")) return "yahoo";
  if (host.includes("duckduckgo.")) return "duckduckgo";
  if (host.includes("yandex.")) return "yandex";

  return "";
}

function getSocialSource(host: string) {
  if (host.includes("instagram.")) return "instagram";
  if (host.includes("t.me") || host.includes("telegram.")) return "telegram";
  if (host.includes("linkedin.")) return "linkedin";
  if (host.includes("twitter.") || host.includes("x.com")) return "x";
  if (host.includes("facebook.")) return "facebook";

  return "";
}

function readAnalyticsProperties(element: HTMLElement) {
  const properties: Record<string, unknown> = {};
  const prefix = "analyticsProperty";

  for (const [key, value] of Object.entries(element.dataset)) {
    if (!key.startsWith(prefix) || !value) continue;

    const propertyKey = key
      .slice(prefix.length)
      .replace(/^[A-Z]/, (match) => match.toLowerCase());
    properties[propertyKey] = value;
  }

  return properties;
}

function postAnalyticsEvent(payload: AnalyticsEventPayload) {
  analyticsQueue.push(payload);

  if (payload.eventName === "purchase_completed" || payload.eventName === "purchase_failed") {
    flushAnalyticsEvents(true);
    return;
  }

  if (analyticsQueue.length >= ANALYTICS_BATCH_MAX_SIZE) {
    flushAnalyticsEvents();
    return;
  }

  scheduleAnalyticsFlush();
}

function scheduleAnalyticsFlush() {
  if (analyticsFlushTimer) return;

  analyticsFlushTimer = window.setTimeout(() => {
    analyticsFlushTimer = null;
    flushAnalyticsEvents();
  }, ANALYTICS_BATCH_DELAY_MS);
}

function flushAnalyticsEvents(useBeacon = false) {
  if (analyticsFlushTimer) {
    window.clearTimeout(analyticsFlushTimer);
    analyticsFlushTimer = null;
  }

  if (!analyticsQueue.length) return;

  const events = analyticsQueue;
  analyticsQueue = [];
  const body = JSON.stringify(
    events.length === 1 ? events[0] : { events },
  );

  if (useBeacon && navigator.sendBeacon) {
    const blob = new Blob([body], { type: "application/json" });
    if (navigator.sendBeacon(ANALYTICS_ENDPOINT, blob)) return;
  }

  void fetch(ANALYTICS_ENDPOINT, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body,
    keepalive: true,
  }).catch(() => {
    // Analytics must never block user flows.
  });
}
